import React, { useCallback, useMemo } from 'react'
import { Case, Default, Switch } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { GuildSolidIcon } from '@/atoms/Icons/GuildSolidIcon'
import { PayItForwardOutlineIcon } from '@/atoms/Icons/PayItForwardOutlineIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { ParagraphMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { JoinTheGuildLivestreamCta } from '@/organisms/Livestream/LivestreamCtaButton/JoinTheGuildLivestreamCta'
import { LivestreamCtaButtonProps } from '@/organisms/Livestream/LivestreamCtaButton/types'
import { useGuildMember } from '@/services/GuildUserService'
import { useSafeTrack } from '@/utils/analytics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

export const LivestreamCtaButton: React.FC<LivestreamCtaButtonProps> = ({ projectSlug, guid, livestreamCta }) => {
  const track = useSafeTrack()
  const { isGuildMember } = useGuildMember()
  const { t } = useTranslate('livestream')

  const onClick = useCallback(() => {
    track('Livestream CTA Clicked', { cta: livestreamCta.ctaType, projectSlug, guid })
  }, [track, livestreamCta, projectSlug, guid])

  const showGuildOption = useMemo(() => {
    return Boolean(isGuildMember && livestreamCta.guildCtaLink && livestreamCta.guildCtaText)
  }, [isGuildMember, livestreamCta.guildCtaLink, livestreamCta.guildCtaText])

  return (
    <Switch>
      <Case condition={livestreamCta.ctaType === 'tickets'}>
        <div>
          <LinkButton
            className="min-w-[190px] rounded"
            href={`${paths.tickets.index}/${projectSlug}?utm_campaign=${projectSlug}-theatrical&utm_medium=web&utm_source=livestream&utm_content=${guid}&utm_term=${projectSlug}`}
            onClick={onClick}
            prefetch={false}
            target="_blank"
            variant="oxide-bright"
          >
            <div className="mx-auto flex flex-row items-center justify-center">
              <TicketIcon color="black" className="mr-2" />
              <Translate i18nKey="getShowtimes" t={t}>
                Get Showtimes
              </Translate>
            </div>
          </LinkButton>
        </div>
      </Case>
      <Case condition={livestreamCta.ctaType === 'guild'}>
        <JoinTheGuildLivestreamCta projectSlug={projectSlug} guid={guid} />
      </Case>
      <Case condition={livestreamCta.ctaType === 'pif'}>
        <div>
          <LinkButton
            className="min-w-[190px] rounded-xl fill-white px-3 py-2 backdrop-blur-md hover:fill-black md:w-fit"
            href={`${paths.payItForward.index}/${projectSlug}?utm_campaign=${projectSlug}-pif&utm_medium=web&utm_source=livestream&utm_content=${guid}&utm_term=${projectSlug}`}
            onClick={onClick}
            prefetch={false}
            target="_blank"
            variant="oxide-core"
          >
            <div className="relative flex w-full flex-row items-center justify-center">
              <PayItForwardOutlineIcon color="black" className="mr-2" />
              <ParagraphMD className="whitespace-nowrap" color="black" weight="semibold">
                <Translate t={t} i18nKey="payItForwardv2">
                  Pay it Forward
                </Translate>
              </ParagraphMD>
            </div>
          </LinkButton>
        </div>
      </Case>
      <Case condition={showGuildOption}>
        <div>
          <LinkButton
            className="min-w-[190px] rounded-xl fill-white px-3 py-2 backdrop-blur-md hover:fill-black md:w-fit"
            href={livestreamCta.guildCtaLink as string}
            onClick={onClick}
            prefetch={false}
            target="_blank"
            variant="oxide-core"
          >
            <div className="relative flex w-full flex-row items-center justify-center">
              <Switch>
                <Case condition={livestreamCta?.icon?.toLowerCase() === 'pay it forward'}>
                  <PayItForwardOutlineIcon color="black" className="mr-2" />
                </Case>
                <Case condition={livestreamCta?.icon?.toLowerCase() === 'guild'}>
                  <GuildSolidIcon color1="gray-950" size={16} className="mr-2" />
                </Case>
                <Case condition={livestreamCta?.icon?.toLowerCase() === 'ticket'}>
                  <TicketIcon color="black" className="mr-2" />
                </Case>
                <Case condition={livestreamCta?.icon?.toLowerCase() === 'none'}>{null}</Case>
                <Default>{null}</Default>
              </Switch>
              <ParagraphMD className="whitespace-nowrap" color="black" weight="semibold">
                {livestreamCta.guildCtaText}
              </ParagraphMD>
            </div>
          </LinkButton>
        </div>
      </Case>
      <Case condition={!showGuildOption && livestreamCta.ctaLink && livestreamCta.ctaText}>
        <div>
          <LinkButton
            className="min-w-[190px] rounded-xl fill-white px-3 py-2 backdrop-blur-md hover:fill-black md:w-fit"
            href={livestreamCta.ctaLink as string}
            onClick={onClick}
            prefetch={false}
            target="_blank"
            variant="oxide-core"
          >
            <div className="relative flex w-full flex-row items-center justify-center">
              <Switch>
                <Case condition={livestreamCta?.guildIcon?.toLowerCase() === 'pay it forward'}>
                  <PayItForwardOutlineIcon color="black" className="mr-2" />
                </Case>
                <Case condition={livestreamCta?.guildIcon?.toLowerCase() === 'guild'}>
                  <GuildSolidIcon color1="gray-950" size={16} className="mr-2" />
                </Case>
                <Case condition={livestreamCta?.guildIcon?.toLowerCase() === 'ticket'}>
                  <TicketIcon color="black" className="mr-2" />
                </Case>
                <Case condition={livestreamCta?.guildIcon?.toLowerCase() === 'none'}>{null}</Case>
                <Default>{null}</Default>
              </Switch>
              <ParagraphMD className="whitespace-nowrap" color="black" weight="semibold">
                {livestreamCta.ctaText}
              </ParagraphMD>
            </div>
          </LinkButton>
        </div>
      </Case>
      <Default>
        <JoinTheGuildLivestreamCta projectSlug={projectSlug} guid={guid} />
      </Default>
    </Switch>
  )
}
